import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { dashboardPath } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const Logo = ({ light = false, size = "md" }) => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
    <img
      src="/carenest-mark.png"
      alt="CARENEST"
      className={size === "lg" ? "h-14 w-14" : "h-11 w-11"}
      style={{ objectFit: "contain" }}
    />
    <span className="leading-none">
      <span className={`block font-display text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-emerald-900"}`}>
        care<span className="text-lime-500">nest</span>
      </span>
      <span className={`mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.14em] ${light ? "text-emerald-300" : "text-emerald-700/70"}`}>
        Care connected. Life supported.
      </span>
    </span>
  </Link>
);

const LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Providers", href: "#providers" },
  { label: "Safety", href: "#safety" },
  { label: "Referrals", href: "#referrals" },
  { label: "FAQ", href: "#faq" },
];

export const SiteNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-emerald-800"
              data-testid={`nav-${l.href.slice(1)}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              <Button
                variant="ghost"
                className="text-emerald-900"
                onClick={() => navigate(dashboardPath(user.role))}
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
              <Button variant="outline" onClick={logout} data-testid="nav-logout-btn">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                Log in
              </Button>
              <Button
                className="rounded-full bg-emerald-900 px-6 hover:bg-emerald-800"
                onClick={() => navigate("/register")}
                data-testid="nav-register-btn"
              >
                Get started
              </Button>
            </>
          )}
        </div>
        <button
          className="lg:hidden text-emerald-900"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 lg:hidden" data-testid="nav-mobile-panel">
          <div className="flex flex-col gap-3">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-1 text-sm text-slate-700">
                {l.label}
              </a>
            ))}
            {user ? (
              <>
                <Button onClick={() => navigate(dashboardPath(user.role))} data-testid="nav-mobile-dashboard">
                  Dashboard
                </Button>
                <Button variant="outline" onClick={logout}>
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate("/login")} data-testid="nav-mobile-login">
                  Log in
                </Button>
                <Button className="bg-emerald-900" onClick={() => navigate("/register")} data-testid="nav-mobile-register">
                  Get started
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="relative overflow-hidden bg-emerald-900 text-emerald-50/80">
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
      <div className="grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <Logo light size="lg" />
          <p className="mt-4 max-w-sm text-sm leading-relaxed">
            CARENEST connects Nigerian families with verified healthcare professionals for dignified care at home —
            protected by escrow and witness verification.
          </p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-300">
            Health services · Trusted providers · Better care
          </p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">Platform</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="#how-it-works">How it works</a></li>
            <li><a href="#safety">Safety & verification</a></li>
            <li><a href="#escrow">Escrow protection</a></li>
            <li><a href="#referrals">Referral programme</a></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">Get started</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/register">Create an account</Link></li>
            <li><Link to="/login">Log in</Link></li>
            <li><Link to="/register">Join as a provider</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">Legal</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/terms" data-testid="footer-terms-link">Terms & Conditions</Link></li>
            <li><Link to="/privacy" data-testid="footer-privacy-link">Privacy & NDPA</Link></li>
            <li><Link to="/fees" data-testid="footer-fees-link">Fees & settlement</Link></li>
          </ul>
        </div>
      </div>
      <div className="mt-12 border-t border-emerald-800 pt-6 text-xs text-emerald-200/70">
        © {new Date().getFullYear()} CARENEST Nigeria — operated by Care Solutions Limited, Enugu. A technology
        marketplace, not a healthcare provider.
      </div>
    </div>
  </footer>
);
